import React, { useLayoutEffect, useRef, useState } from 'react';
import { styled } from 'baseui';
import { useAtomValue } from 'jotai';
import type { AnnotationStyleT } from '../../../../../common/types/types';
import { isVideoLabelingM4EnabledAtom } from '../../../../labelling-v2/atoms/m4-atoms';
import { CSS_VARIABLE_STAGE_ROTATION, CSS_VARIABLE_STAGE_VISIBLE_SCALE } from '../../../constants/stage-css-variables';
import { LabelText as Text } from '../common/label-text';
import { VERTEX_RADIUS } from './vertex';
const LABEL_PILL_PADDING_X = 6;
const LABEL_PILL_PADDING_Y = 3;
const LABEL_PILL_BORDER_RADIUS = 4;
const TEXT_POINT_MARGIN = 8;
const Circle = styled<'circle', {
  $originX: number;
  $originY: number;
}>('circle', ({
  $originX,
  $originY
}) => ({
  transformOrigin: `${$originX}px ${$originY}px`,
  transform: `scale(var(${CSS_VARIABLE_STAGE_VISIBLE_SCALE}))`
}));
const LabelPillGroup = styled<'g', {
  $originX: number;
  $originY: number;
}>('g', ({
  $originX,
  $originY
}) => ({
  transformOrigin: `${$originX}px ${$originY}px`,
  transform: `rotate(calc(-1 * var(${CSS_VARIABLE_STAGE_ROTATION}))) scale(var(${CSS_VARIABLE_STAGE_VISIBLE_SCALE}))`
}));
const LabelPillText = styled('text', ({
  $theme
}) => ({
  ...$theme.typography.LabelXSmall,
  direction: 'ltr',
  userSelect: 'none',
  dominantBaseline: 'text-before-edge'
}));
type Props = {
  annotationStyle: AnnotationStyleT;
  label: string;
  labelCoords: [number, number] | undefined | null;
  hideLabelPointer?: boolean;
};
const AnnotationLabel = ({
  label,
  labelCoords,
  annotationStyle,
  hideLabelPointer
}: Props) => {
  const isVideoLabelingM4Enabled = useAtomValue(isVideoLabelingM4EnabledAtom);
  const [LABEL_POINT_X, LABEL_POINT_Y] = labelCoords || [0, 0];
  const textRef = useRef<SVGTextElement>(null);
  const [textWidth, setTextWidth] = useState(0);
  const [textHeight, setTextHeight] = useState(0);
  useLayoutEffect(() => {
    if (textRef.current) {
      const bbox = textRef.current.getBBox();
      setTextWidth(bbox.width);
      setTextHeight(bbox.height);
    }
  }, [label]);
  if (!isVideoLabelingM4Enabled) {
    const LABEL_TEXT_X = LABEL_POINT_X + VERTEX_RADIUS + TEXT_POINT_MARGIN;
    const LABEL_TEXT_Y = LABEL_POINT_Y + VERTEX_RADIUS;
    return <>
        {!hideLabelPointer && <Circle cx={LABEL_POINT_X} cy={LABEL_POINT_Y} fill={annotationStyle.strokeColor} r={VERTEX_RADIUS} $originX={LABEL_POINT_X} $originY={LABEL_POINT_Y} />}
        <Text x={LABEL_TEXT_X} y={LABEL_TEXT_Y} $originX={LABEL_POINT_X} $originY={LABEL_POINT_Y} $outlined>
          {label}
        </Text>
      </>;
  }
  const pillHeight = textHeight + LABEL_PILL_PADDING_Y * 2;
  const pillWidth = textWidth + LABEL_PILL_PADDING_X * 2;
  const pillX = LABEL_POINT_X + VERTEX_RADIUS + TEXT_POINT_MARGIN;
  const pillY = LABEL_POINT_Y - pillHeight / 2;
  return <>
      <LabelPillGroup $originX={LABEL_POINT_X} $originY={LABEL_POINT_Y}>
        <rect x={pillX} y={pillY} width={pillWidth} height={pillHeight} rx={LABEL_PILL_BORDER_RADIUS} fill={annotationStyle.strokeColor} opacity={textWidth ? 1 : 0} />
        <LabelPillText ref={textRef} x={pillX + LABEL_PILL_PADDING_X} y={pillY + LABEL_PILL_PADDING_Y} fill={annotationStyle.fillColor}>
          {label}
        </LabelPillText>
      </LabelPillGroup>
      {!hideLabelPointer && <Circle cx={LABEL_POINT_X} cy={LABEL_POINT_Y} fill={annotationStyle.strokeColor} stroke={isVideoLabelingM4Enabled ? annotationStyle.fillColor : undefined} strokeWidth={isVideoLabelingM4Enabled ? 1 : 0} r={VERTEX_RADIUS} $originX={LABEL_POINT_X} $originY={LABEL_POINT_Y} />}
    </>;
};
export default AnnotationLabel;